import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";

const ReCAPTCHA_KEY = "6Lfhv_QqAAAAAEIbNo043A9-otuDjPSyQEo335V0"; // Replace with your reCAPTCHA site key!

const insuranceOptions = [
    { value: "Carelon Behavioral Health", label: "Carelon Behavioral Health" },
    { value: "Quest Behavioral Health", label: "Quest Behavioral Health" },
    { value: "Aetna", label: "Aetna" },
    { value: "Cigna", label: "Cigna" },
    { value: "Tricare", label: "Tricare" },
    { value: "United Healthcare (Optum)", label: "United Healthcare (Optum)" },
    { value: "Blue Cross Blue Shield of Massachusetts", label: "Blue Cross Blue Shield of Massachusetts" },
    { value: "Other", label: "Other" },
];

const ContactForm = () => {
    const [showInsurers, setShowInsurers] = useState(false);
    const [showOtherInsurer, setShowOtherInsurer] = useState(false);
    const [isComingSoonOption, setComingSoonOption] = useState(false);
    const [formStatus, setFormStatus] = useState(null);
    const [recaptchaToken, setRecaptchaToken] = useState(null);

    const handleRecaptchaChange = (token) => {
        setRecaptchaToken(token);
    };

    const validationSchema = Yup.object({
        fname: Yup.string().required("First name is required"),
        lname: Yup.string().required("Last name is required"),
        phone: Yup.string()
            .matches(/^\d{10}$/, "Phone number must be 10 digits")
            .required("Phone number is required"),
        email: Yup.string().email("Invalid email format").required("Email is required"),
        dob: Yup.date().required("Date of birth is required"),
        useInsurance: Yup.string().required("Please select if you will use insurance"),
        insurance: Yup.string().when("useInsurance", {
            is: "Yes",
            then: Yup.string().required("Please select an insurance provider"),
        }),
        otherInsurance: Yup.string().when("insurance", {
            is: "Other",
            then: Yup.string().required("Please enter your insurance provider"),
        }),
        message: Yup.string().max(1000, "Message cannot exceed 1000 characters").required("Message is required"),
        website: Yup.string().max(0, "Spam detected."),
    });

    const handleUseInsuranceChange = (e, setFieldValue) => {
        const value = e.target.value;
        setFieldValue("useInsurance", value);
        if (value === "Yes") {
            setShowInsurers(true);
        } else {
            setShowInsurers(false);
            setShowOtherInsurer(false);
            setComingSoonOption(false);
            setFieldValue("insurance", "");
            setFieldValue("otherInsurance", "");
        }
    };

    const handleInsuranceChange = (option, setFieldValue) => {
        const selected = option ? option.value : "";
        setFieldValue("insurance", selected);
        setFieldValue("otherInsurance", "");

        if (selected === "Other") {
            setShowOtherInsurer(true);
            setComingSoonOption(false);
        } else if (["United Healthcare (Optum)", "Blue Cross Blue Shield of Massachusetts"].includes(selected)) {
            setShowOtherInsurer(false);
            setComingSoonOption(true);
        } else {
            setShowOtherInsurer(false);
            setComingSoonOption(false);
        }
    };

    const submitForm = async (values, { setSubmitting, resetForm }) => {
        if (!recaptchaToken) {
            setFormStatus({ success: false, message: "Please verify the reCAPTCHA." });
            setSubmitting(false);
            return;
        }

        try {
            setFormStatus(null);

            const payload = {
                ...values,
                recaptchaToken,
            };

            const response = await fetch(`${window.myLocalizedData.restUrl}contact-form/v1/submit`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-WP-Nonce": window.myLocalizedData.nonce,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok) {
                setFormStatus({ success: true, message: "Form submitted successfully!" });
                resetForm();
                setShowInsurers(false);
                setShowOtherInsurer(false);
                setComingSoonOption(false);
            } else {
                setFormStatus({ success: false, message: data.message || "Submission failed. Please try again." });
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            setFormStatus({ success: false, message: "An error occurred. Please try again." });
        }

        setSubmitting(false);
    };

    return (
        <section id="contact" className="page-section">
            <h2>Start Your Journey</h2>
            <h3>We're waiting for you!</h3>

            <Formik
                initialValues={{
                    fname: "",
                    lname: "",
                    phone: "",
                    email: "",
                    dob: "",
                    useInsurance: "",
                    insurance: "",
                    otherInsurance: "",
                    message: "",
                    website: "",
                }}
                enableReinitialize
                validationSchema={validationSchema}
                validateOnMount
                onSubmit={submitForm}
            >
                {({  values, touched, errors, setFieldValue, isSubmitting, isValid }) => (
                    <Form className="contact-form">
                        <div>Name:</div>
                        <div className="row">
                            <div className="col">
                                <Field placeholder="First Name" name="fname" autocomplete="on" />
                                <ErrorMessage name="fname" component="div" className="error-message" />
                            </div>
                            <div className="col">
                                <Field placeholder="Last Name" name="lname" autocomplete="on" />
                                <ErrorMessage name="lname" component="div" className="error-message" />
                            </div>
                        </div>

                        <div style={{ display: "none" }}>
                            <Field name="website" placeholder="Your website (leave empty)" />
                        </div>

                        <div>Contact:</div>
                        <div className="row">
                            <div className="col">
                                <Field placeholder="Phone Number" type="tel" name="phone" autocomplete="on" />
                                <ErrorMessage name="phone" component="div" className="error-message" />
                            </div>
                            <div className="col">
                                <Field placeholder="Email Address" type="email" name="email" autocomplete="on" />
                                <ErrorMessage name="email" component="div" className="error-message" />
                            </div>
                        </div>

                        <div>Date of Birth:</div>
                        <Field type="date" name="dob" autocomplete="on" />
                        <ErrorMessage name="dob" component="div" className="error-message" />

                        <div>Will you use insurance?</div>
                        <Field
                            autocomplete="on"
                            as="select"
                            name="useInsurance"
                            onChange={(e) => handleUseInsuranceChange(e, setFieldValue)}
                        >
                            <option value="">Please Select An Option</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No, I will pay out of pocket.</option>
                        </Field>
                        <ErrorMessage name="useInsurance" component="div" className="error-message" />

                        {showInsurers && (
                            <>
                                <div>Select your insurance provider:</div>
                                <Select
                                    autocomplete="on"
                                    options={insuranceOptions}
                                    onChange={(option) => handleInsuranceChange(option, setFieldValue)}
                                    placeholder="Select insurance..."
                                    isClearable
                                />
                                {isComingSoonOption && (
                                    <div className="text-red-500 mt-2">
                                        This insurance provider is coming soon.
                                    </div>
                                )}
                                <ErrorMessage name="insurance" component="div" className="error-message" />
                            </>
                        )}

                        {showOtherInsurer && (
                            <>
                                <div>Enter your insurance provider:</div>
                                <Field placeholder="Other Insurance" name="otherInsurance" autocomplete="on" />
                                <ErrorMessage name="otherInsurance" component="div" className="error-message" />
                            </>
                        )}

                        <div>Message:</div>
                        <Field
                            autocomplete="on"
                            as="textarea"
                            rows="5"
                            name="message"
                            placeholder="What else would you like to share with us?"
                        />
                        <ErrorMessage name="message" component="div" className="error-message" />

                        <div>
                            <ReCAPTCHA
                                sitekey={ReCAPTCHA_KEY}
                                onChange={handleRecaptchaChange}
                                className="mt-4"
                            />
                        </div>


                     {/*   <pre>values: {JSON.stringify(values,null,2)}</pre>
                        <pre>is Valid: {JSON.stringify(isValid)}</pre>
                        <pre>errors: {JSON.stringify(errors, null, 2)}</pre>
                        <pre>touched: {JSON.stringify(touched, null, 2)}</pre>
*/}
                        <div className="pt-4">
                            <button
                                className="primary-link"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Sending..." : "Send!"}
                            </button>
                        </div>

                        {formStatus && (
                            <div
                                className={`mt-4 ${
                                    formStatus.success ? "text-green-600" : "text-red-600"
                                }`}
                            >
                                {formStatus.message}
                            </div>
                        )}
                    </Form>
                )}
            </Formik>
        </section>
    );
};

export default ContactForm;
